import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ExternalLink, Sparkles, AlertTriangle, Calendar } from "lucide-react";

const INTERVAL_LABEL = {
  month: "Mensile",
  year: "Annuale",
};

const STATUS_LABEL = {
  active: "Attivo",
  trialing: "In prova",
  past_due: "Pagamento in ritardo",
  unpaid: "Non pagato",
  canceled: "Cancellato",
  incomplete: "Incompleto",
  incomplete_expired: "Scaduto",
};

function formatEuro(cents, currency) {
  if (cents == null) return "";
  const val = (cents / 100).toFixed(2).replace(".", ",");
  const sym = (currency || "eur").toLowerCase() === "eur" ? "€" : (currency || "").toUpperCase() + " ";
  return `${sym}${val}`;
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function ManageSubscriptionCard() {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/payments/me/subscription")
      .then((r) => setSub(r.data))
      .catch(() => setSub(null))
      .finally(() => setLoading(false));
  }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/portal", {
        return_url: `${window.location.origin}/profile`,
      });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile aprire il portale");
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-[#2A2A35] p-6 mt-6" data-testid="subscription-card-loading">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#A0A0AB]">Abbonamento</div>
      </div>
    );
  }

  // No customer yet → invite to subscribe
  if (!sub || !sub.has_customer) {
    return (
      <div className="border border-[#2A2A35] p-6 mt-6 relative overflow-hidden" data-testid="subscription-card-inactive">
        <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#FF3B30]/10 rounded-full blur-2xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-4 h-4 text-[#E2FF31]" />
            <h2 className="font-display text-2xl font-black uppercase tracking-tight">
              Abbonamento
            </h2>
          </div>
          <p className="text-sm text-[#A0A0AB] mb-4">
            Sblocca l'AI Coach illimitato, i programmi personalizzati e la progressione automatica.
          </p>
          <Link
            to="/subscribe"
            data-testid="go-to-subscribe-btn"
            className="inline-flex items-center gap-2 bg-[#FF3B30] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#e02e24] transition-all text-xs"
          >
            <CreditCard className="w-4 h-4" /> Diventa Pro
          </Link>
        </div>
      </div>
    );
  }

  const plan = sub.plan || {};
  const interval = INTERVAL_LABEL[plan.interval] || "—";
  const statusLabel = STATUS_LABEL[sub.status] || sub.status || "—";
  const renewalDate = formatDate(sub.current_period_end);
  const isActive = sub.active;
  const cancelling = sub.cancel_at_period_end;

  return (
    <div className="border border-[#2A2A35] p-6 mt-6 relative overflow-hidden" data-testid="subscription-card-active">
      <div className="absolute -top-8 -right-8 w-32 h-32 bg-[#E2FF31]/10 rounded-full blur-2xl" />
      <div className="relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#E2FF31]" />
            <h2 className="font-display text-2xl font-black uppercase tracking-tight">
              Abbonamento
            </h2>
          </div>
          <span
            data-testid="sub-status-badge"
            className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 border ${
              isActive
                ? "border-[#E2FF31] text-[#E2FF31]"
                : "border-[#FF3B30] text-[#FF3B30]"
            }`}
          >
            {statusLabel}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-1">Piano</div>
            <div className="font-display text-xl font-black" data-testid="sub-plan-interval">
              {interval}
            </div>
            {plan.unit_amount != null && (
              <div className="text-xs text-[#A0A0AB]">
                {formatEuro(plan.unit_amount, plan.currency)} / {plan.interval === "year" ? "anno" : "mese"}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#A0A0AB] mb-1 flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {cancelling ? "Termina il" : "Rinnovo il"}
            </div>
            <div className="font-display text-xl font-black" data-testid="sub-renewal-date">
              {renewalDate || "—"}
            </div>
          </div>
        </div>

        {cancelling && (
          <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/10 p-3 mb-4 flex items-start gap-2" data-testid="sub-cancelling-warning">
            <AlertTriangle className="w-4 h-4 text-[#FF3B30] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#FFB3AE]">
              Cancellazione programmata. L'accesso Pro resterà attivo fino al <b>{renewalDate}</b>. Puoi riattivare l'abbonamento dal portale.
            </div>
          </div>
        )}

        <button
          onClick={openPortal}
          disabled={busy}
          data-testid="manage-subscription-btn"
          className="inline-flex items-center gap-2 bg-white text-black font-bold uppercase tracking-widest px-6 py-3 hover:bg-[#E2FF31] transition-all text-xs disabled:opacity-40"
        >
          <ExternalLink className="w-4 h-4" />
          {busy ? "Apertura..." : "Gestisci abbonamento"}
        </button>
        <div className="text-[10px] uppercase tracking-widest text-[#A0A0AB] mt-3">
          Cambia metodo di pagamento · Scarica fatture · Passa da mensile ad annuale · Cancella
        </div>
      </div>
    </div>
  );
}
